import { X, Instagram, Facebook } from 'lucide-react';
import { FaTwitter, FaTiktok } from 'react-icons/fa';

interface WelcomePopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function WelcomePopup({ isOpen, onClose }: WelcomePopupProps) {
  if (!isOpen) return null;

  const handleSocialClick = (platform: string) => {
    // Track which social platform was clicked (could be used for analytics later)
    console.log(`User clicked ${platform} from welcome popup`);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 relative animate-in fade-in zoom-in duration-300">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className="p-6 text-center">
          <div className="mb-4">
            <img src="/logo.jpeg" alt="BlackBook Logo" className="h-16 w-16 object-contain rounded-md mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome to BlackBook! 👋
            </h2>
            <p className="text-gray-600 text-sm">
              Join our community and stay connected with the latest updates on Black-owned businesses
            </p>
          </div>

          {/* Social Media Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Follow Us for Updates</h3>
            <div className="grid grid-cols-2 gap-3">
              <a 
                href="https://www.instagram.com/theeeblackbook?igsh=NjBkajJkcTlpNTdx&utm_source=qr" 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={() => handleSocialClick('Instagram')}
                className="flex items-center justify-center space-x-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white p-3 rounded-lg hover:opacity-90 transition-opacity"
              >
                <Instagram size={20} />
                <span className="font-medium">Instagram</span>
              </a>
              
              <a 
                href="https://www.facebook.com/profile.php?id=61578640042420" 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={() => handleSocialClick('Facebook')}
                className="flex items-center justify-center space-x-2 bg-blue-600 text-white p-3 rounded-lg hover:opacity-90 transition-opacity"
              >
                <Facebook size={20} />
                <span className="font-medium">Facebook</span>
              </a>
              
              <a 
                href="https://twitter.com/theeeblackbook" 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={() => handleSocialClick('Twitter')}
                className="flex items-center justify-center space-x-2 bg-black text-white p-3 rounded-lg hover:opacity-90 transition-opacity"
              >
                <FaTwitter size={20} />
                <span className="font-medium">Twitter</span>
              </a>
              
              <a 
                href="https://www.tiktok.com/@theeeblackbook" 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={() => handleSocialClick('TikTok')}
                className="flex items-center justify-center space-x-2 bg-black text-white p-3 rounded-lg hover:opacity-90 transition-opacity"
              >
                <FaTiktok size={20} />
                <span className="font-medium">TikTok</span>
              </a>
            </div>
            <p className="text-xs text-gray-500 mt-3">@theeeblackbook</p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={onClose}
              className="w-full bg-red-700 hover:bg-red-800 text-white py-3 px-4 rounded-lg font-semibold transition-colors"
            >
              Explore Black-Owned Businesses
            </button>
            
            <button
              onClick={onClose}
              className="w-full text-gray-500 hover:text-gray-700 py-2 text-sm transition-colors"
            >
              Maybe later
            </button>
          </div>

          {/* Bottom text */}
          <p className="text-xs text-gray-400 mt-4">
            Supporting Black-owned businesses nationwide
          </p>
        </div>
      </div>
    </div>
  );
}